import React, { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDemoDataSeedingState } from "@api/workspace.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useScope } from "@state/scopeContext.lib";
import { Button } from "@design-system/Button/Button";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import { Loader } from "@design-system/Loader/Loader";
import { VStack } from "@design-system/VStack/VStack";
import { ChildrenProps } from "../../types";
import styles from "./DemoDataSeedingGate.module.scss";

export const DemoDataSeedingGate: React.FC<ChildrenProps> = ({ children }) => {
    const { workspaceId, changeActiveOrganization } = useScope();
    const activeOrganization = useActiveOrganization();
    const { isSeeding, isTakingLongerThanUsual } =
        useDemoDataSeedingState(workspaceId);
    const queryClient = useQueryClient();
    const seedingWorkspaceIdRef = useRef<string | null>(null);

    useEffect(() => {
        const didFinishSeeding =
            !isSeeding &&
            !!seedingWorkspaceIdRef.current &&
            seedingWorkspaceIdRef.current === workspaceId;
        if (didFinishSeeding) {
            void queryClient.invalidateQueries();
        }
        seedingWorkspaceIdRef.current = isSeeding
            ? (workspaceId ?? null)
            : null;
    }, [isSeeding, workspaceId, queryClient]);

    return (
        <>
            {children}
            {isSeeding && (
                <div className={styles.cover}>
                    <KickLogo
                        variant="icon"
                        size="lg"
                        label="Kick"
                        className={styles.logo}
                    />
                    <VStack gap={16} align="center" className={styles.content}>
                        <Loader />
                        <h1 className={styles.heading}>Seeding demo data…</h1>
                        <p className={styles.description}>
                            {isTakingLongerThanUsual
                                ? "This is taking longer than usual. Please check back later."
                                : "Seeding demo data usually takes a few minutes. This page refreshes on its own when the data is ready."}
                        </p>
                        {activeOrganization && (
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    changeActiveOrganization(activeOrganization)
                                }
                            >
                                Back to {activeOrganization.name}
                            </Button>
                        )}
                    </VStack>
                </div>
            )}
        </>
    );
};
